import React, { useState, MouseEvent } from 'react';
import { I } from '../icons-ds';
import { Tag, Highlight } from '../ui';
import { formatUpdated } from '../lib/freshness';
import { type SearchSnippet } from '../lib/search';
import type { Phrase } from '../lib/types';

export type PhraseCardLayout = 'list' | 'grid';

export type PhraseCardProps = {
  phrase: Phrase;
  onOpen?: () => void;
  onFavorite?: () => void;
  layout?: PhraseCardLayout;
  /** Matching excerpt to show under the title (search results only). */
  snippet?: SearchSnippet;
  /** Query terms to highlight inside the snippet. */
  terms?: string[];
};

/** Two-line matching excerpt shown on search-result cards. */
function Snippet({ snippet, terms }: { snippet: SearchSnippet; terms: string[] }) {
  return (
    <div
      className="t-footnote"
      style={{
        marginTop: 6, color: 'var(--fg-tertiary)', lineHeight: 1.4,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}
    >
      <span style={{ color: 'var(--fg-quaternary)', fontWeight: 600 }}>{snippet.sectionTitle}: </span>
      <Highlight text={snippet.text} terms={terms} />
    </div>
  );
}

export function PhraseCard({ phrase, onOpen, onFavorite, layout = 'list', snippet, terms = [] }: PhraseCardProps) {
  const [hover, setHover] = useState(false);
  const isFav = phrase.favorite;

  const favBtn = (iconSize: number) => (
    <button
      className="dp-phrase-favorite"
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onFavorite?.();
      }}
      aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
      aria-pressed={isFav}
      style={{
        color: isFav ? 'var(--warning)' : 'var(--fg-tertiary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 44, minHeight: 44, flexShrink: 0,
        background: 'transparent', border: 0, cursor: 'pointer', padding: 0,
      }}
    >
      {isFav ? <I.starFill size={iconSize} /> : <I.star size={iconSize} />}
    </button>
  );

  if (layout === 'grid') {
    return (
      <div
        onClick={onOpen}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          background: 'var(--bg-surface)', borderRadius: 'var(--r-lg)',
          padding: 18, cursor: 'pointer',
          border: '1px solid var(--line-divider)',
          boxShadow: hover ? 'var(--shadow-2)' : 'var(--shadow-1)',
          transform: hover ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'all var(--dur-fast) var(--ease-out)',
          display: 'flex', flexDirection: 'column', gap: 10, minHeight: 156,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <Tag kind={phrase.primaryTag}>{phrase.tags[0]}</Tag>
          {favBtn(18)}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.3px', marginBottom: 4 }}>
            {phrase.name}
          </div>
          <div className="t-mono" style={{ color: 'var(--accent)' }}>{phrase.trigger}</div>
          {snippet && <Snippet snippet={snippet} terms={terms} />}
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: 8, borderTop: '1px solid var(--line-divider)',
          }}
        >
          <span className="t-caption1" style={{ color: 'var(--fg-tertiary)' }}>
            {phrase.sections.length} {phrase.sections.length === 1 ? 'section' : 'sections'}
            {phrase.updated ? ` · ${formatUpdated(phrase.updated)}` : ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="dp-phrase-card"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--bg-surface)', borderRadius: 'var(--r-lg)',
        padding: '14px 18px', cursor: 'pointer',
        border: '1px solid var(--line-divider)',
        boxShadow: hover ? 'var(--shadow-2)' : 'var(--shadow-1)',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'all var(--dur-fast) var(--ease-out)',
        display: 'flex', alignItems: 'center', gap: 16,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <Tag kind={phrase.primaryTag}>{phrase.tags[0]}</Tag>
          <span className="t-mono" style={{ color: 'var(--fg-tertiary)', overflowWrap: 'anywhere' }}>{phrase.trigger}</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.3px' }}>
          {phrase.name}
        </div>
        {snippet && <Snippet snippet={snippet} terms={terms} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <span
          style={{
            width: 44, height: 44, borderRadius: 8,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all var(--dur-fast) var(--ease-out)',
          }}
        >
          {favBtn(18)}
        </span>
        <I.chevR size={18} style={{ color: 'var(--fg-quaternary)' }} />
      </div>
    </div>
  );
}

export default PhraseCard;
